import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import type { OrganizationUserAdminDTO } from "@/orval/model";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import {
  adminParentGetParentsFromOrganization,
  getAdminParentGetParentsFromOrganizationQueryKey,
  getAdminParentGetParentsFromOrganizationQueryOptions,
} from "@/orval/admin-parent";
import { createFileRoute } from "@tanstack/react-router";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { EditOrganizationUserDialog } from "./-components/edit-organization-user-dialog";
import { useState } from "react";
import { PortalPageHeader } from "@/components/portal-page-header";

const columns: CustomColumnDef<OrganizationUserAdminDTO>[] = [
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    accessorFn: (row) => `${row.firstName || "N/A"} ${row.lastName || ""}`,
    id: "fullName",
  },
  {
    header: "Email",
    accessorFn: (row) => row.email,
    id: "email",
  },
  {
    header: "Phone",
    accessorFn: (row) => row.phoneNumber,
    id: "phoneNumber",
  },
];

const OrganizationAdminParentsPage: React.FC = () => {
  const { organizationId } = Route.useParams();

  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserAdminDTO | null>(null);

  const handleRowClick = (parent: OrganizationUserAdminDTO) => {
    setSelectedUser(parent);
    setDialogOpen(true);
  };

  const getParents = adminParentGetParentsFromOrganization;

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader className="mb-8" title="Parents" />

      <DataTable
        columns={columns}
        queryFn={(params) => getParents(organizationId, params)}
        queryKeyFn={(params) => getAdminParentGetParentsFromOrganizationQueryKey(organizationId, params)}
        onRowClick={handleRowClick}
        getRowIdFn={(row) => row.id.toString()}
      />

      {selectedUser && (
        <EditOrganizationUserDialog isOpen={isDialogOpen} onClose={() => setDialogOpen(false)} user={selectedUser} />
      )}
    </div>
  );
};

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/parents")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) => {
    const { searchString } = searchStringSearchSchema.parse(location.search);
    return ensureRouteQueryData(
      loaderContext,
      getAdminParentGetParentsFromOrganizationQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions({ searchString }),
      ),
    );
  },
  component: OrganizationAdminParentsPage,
});
